<template>
  <el-collapse-item name="glip">
    <template #title>
      <div>
        <div class="el-collapse-item__image">
          <img src="./img/glip-logo.svg" >
        </div>
        <span class="el-collapse-item__title">Glip</span>
        <span class="el-collapse-item__sub-title">Plan, share, & organize work in real-time</span>
      </div>
    </template>
    <div class="glip-integration">
      <h4>Send notifications to Glip</h4>
      <p>When you create items, add comments, or update items in any way, DoneDone will post a new message to one of your Glip conversations.</p>
      <p><br></p>
      <ol>
        <li>
          Login to Glip and choose
          <strong>Integrations.</strong>
        </li>
        <li>
          Choose <strong>DoneDone</strong> from the list of available integrations.
        </li>
        <li>
          Choose a conversation and click
          <strong>Add Integration.</strong>
        </li>
        <li>
          Copy and paste the integration’s
          <strong>API Token</strong> below:
        </li>
      </ol>
      <el-form ref="form" :disabled="isSubmitting || isRemoving" :model="formData" :rules="rules">
        <el-form-item prop="apiToken">
          <standard-filter-input v-model="formData.apiToken" placeholder="API Token..." />
        </el-form-item>
      </el-form>
      <dd-button
        :is-disabled="!glipIntegration.apiToken || isSubmitting"
        :is-loading="isRemoving"
        class="float-left"
        type="flat"
        size="medium"
        @click="removeHandler"
      >Remove Glip</dd-button>
      <dd-button
        :is-disabled="isRemoving"
        :is-loading="isSubmitting"
        :color="buttonColor"
        class="float-right"
        size="medium"
        @click="submitHandler"
      >Save Changes</dd-button>
      <div class="clearfix" />
    </div>
  </el-collapse-item>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  IGenericJSONMessage,
  IProjectsOutputIntegrationsGlipIntegrationInfo,
} from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'

export default defineComponent({
  name: 'GlipIntegrationAccordion',

  props: {
    type: {
      type: String as () => EEntities,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
    glipIntegration: {
      type: Object as () => IProjectsOutputIntegrationsGlipIntegrationInfo,
      required: true,
    },
  },

  data() {
    return {
      isSubmitting: false,
      isRemoving: false,
      formData: {
        apiToken: '',
      },
    }
  },

  computed: {
    buttonColor(): string {
      return ColorAssistant.getEntityColor(this.type)
    },

    rules() {
      return {
        apiToken: [
          {
            required: true,
            trigger: 'submit',
            message: 'API Token is required',
          },
        ],
      }
    },
  },

  watch: {
    glipIntegration: {
      immediate: true,
      handler(newVal) {
        this.formData.apiToken = newVal.apiToken
      },
    },
  },

  methods: {
    async save(apiToken: string): Promise<IGenericJSONMessage> {
      if (this.type === EEntities.MAILBOXES) {
        return await MailboxesAPI.post_SetGlipIntegration(
          this.entityId,
          apiToken,
        )
      } else {
        return await InternalProjectsAPI.post_SetGlipIntegration(
          this.entityId,
          apiToken,
        )
      }
    },

    async remove(): Promise<IGenericJSONMessage> {
      if (this.type === EEntities.MAILBOXES) {
        return await MailboxesAPI.post_RemoveGlipIntegration(this.entityId)
      } else {
        return await InternalProjectsAPI.post_RemoveGlipIntegration(
          this.entityId,
        )
      }
    },

    async submitHandler() {
      // @ts-ignore
      this.$refs.form.validate(async valid => {
        if (valid) {
          this.isSubmitting = true
          try {
            const response = await this.save(this.formData.apiToken)
            this.$messaging.showSuccess(response.message)
          } catch (err) {
            this.$error.emit(err, true)
          } finally {
            this.isSubmitting = false
          }
        }
      })
    },

    async removeHandler() {
      this.isRemoving = true
      try {
        const response = await this.remove()
        this.$messaging.showSuccess(response.message)
        this.formData.apiToken = ''
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isRemoving = false
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.glip-integration {
  .el-form {
    margin-top: 12px;
  }
}
</style>
